import { Component, inject, OnDestroy, OnInit, signal } from '@angular/core';
import { CurrentUserRoleAndLogin } from "../../../core/types/auth.types";
import { AuthService } from "../../../core/services/auth.service";
import { baseBottomOffset, dailyFeedbackReportsResourceType } from "./configs/daily-feedback-reports-page-config";
import { ReactiveFormsModule } from "@angular/forms";
import {
  DateRange
} from "./types/daily-feedback-reports-page.types";
import { defaultDateRange } from "./configs/daily-feedback-reports-filters-config";
import { DailyFeedbackReportsService } from "./services/daily-feedback-reports.service";
import { DailyFeedbackReportsPaginationDataService } from "./services/daily-feedback-reports-pagination-data.service";
import { LoadingService } from "../../../core/services/loading.service";
import { Subscription } from "rxjs";
import { DailyFeedbackReportsDataService } from "./services/daily-feedback-reports-data.service";
import { DEFAULT_ZOOM } from "../../../core/constants/constants";
import { LoaderComponent } from "../../../shared/components/loader/loader.component";
import {
  DailyFeedbackReportsFiltersComponent
} from "./components/daily-feedback-reports-filters/daily-feedback-reports-filters.component";
import {
  DailyFeedbackReportsPageContentComponent
} from "./components/daily-feedback-reports-page-content/daily-feedback-reports-page-content.component";
import {
  DailyFeedbackReportsPagePaginationComponent
} from "./components/daily-feedback-reports-page-pagination/daily-feedback-reports-page-pagination.component";
import { PageInfoComponent } from "../../../core/components/page-info/page-info.component";

@Component({
  selector: 'app-daily-feedback-reports-page',
  standalone: true,
  imports: [
    ReactiveFormsModule,
    LoaderComponent,
    DailyFeedbackReportsFiltersComponent,
    DailyFeedbackReportsPageContentComponent,
    DailyFeedbackReportsPagePaginationComponent,
    PageInfoComponent
  ],
  templateUrl: './daily-feedback-reports-page.component.html',
  styleUrl: './daily-feedback-reports-page.component.scss'
})
export class DailyFeedbackReportsPageComponent implements OnInit, OnDestroy {
  public dailyFeedbackReportsPaginationDataService: DailyFeedbackReportsPaginationDataService = inject(DailyFeedbackReportsPaginationDataService);
  public dailyFeedbackReportsDataService: DailyFeedbackReportsDataService = inject(DailyFeedbackReportsDataService);
  private authService: AuthService = inject(AuthService);
  private dailyFeedbackReportsService: DailyFeedbackReportsService = inject(DailyFeedbackReportsService);
  private loadingService: LoadingService = inject(LoadingService);

  public readonly dailyFeedbackReportsResourceType = dailyFeedbackReportsResourceType;
  public loginAndRoles = signal<CurrentUserRoleAndLogin | null>(null);
  public dateRange = signal<DateRange>(defaultDateRange);
  public isLoading = signal<boolean>(false);
  public groupStyles = signal<Record<string, string>>({});
  private subscriptions: Subscription = new Subscription();

  public ngOnInit(): void {
    this.loginAndRoles.set(this.authService.getRolesAndLogin());
    this.initSubscriptions();
  }

  public ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  public async selectDate(dateRange: DateRange): Promise<void> {
    this.dateRange.set(dateRange);
    this.dailyFeedbackReportsPaginationDataService.resetPaginationProps();
    await this.loadData();
  }

  public async selectedPage(): Promise<void> {
    await this.loadData();
  }

  public updateGroupStyles(currentZoomOption: number): void {
    const scaleFactor = 100 / currentZoomOption;
    const scaledWidthHeight = 100 * scaleFactor;
    const height = `calc(${scaledWidthHeight}vh - ${baseBottomOffset * scaleFactor}px)`;

    this.groupStyles.set({
      transform: `scale(${currentZoomOption / 100})`,
      width: `${scaledWidthHeight}%`,
      minHeight: currentZoomOption <= DEFAULT_ZOOM ? height : '',
      maxHeight: currentZoomOption > DEFAULT_ZOOM ? height : ''
    });
  }

  private initSubscriptions() {
    this.subscriptions.add(
      this.loadingService.loadingState$.subscribe({
        next: (isLoading: boolean) => this.isLoading.set(isLoading)
      })
    );
  }

  private async loadData() {
    this.loadingService.setIsLoadingState(true);
    await this.dailyFeedbackReportsService.getDailyFeedbackData(this.dateRange())
      .finally(() => this.loadingService.setIsLoadingState(false));
  }
}
